// src/components/AdminChallengesTab.jsx
import React, { useState, useEffect } from 'react';
import { challengeService } from '../../services/api';

const initialChallengeState = { title: '', description: '', points: 100, active: true };

export default function AdminChallengesTab({ actionLoading, setActionLoading, setError }) {
    const [challenges, setChallenges] = useState([]);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [currentChallenge, setCurrentChallenge] = useState(initialChallengeState);

    // Cargar desafíos al montar
    useEffect(() => {
        loadChallenges();
    }, []);

    const loadChallenges = async () => {
        setError(null);
        try {
            const response = await challengeService.getChallenges();
            setChallenges(response.data || response);
        } catch (err) {
            console.error('Error loading challenges:', err);
            setError('Error al cargar la lista de desafíos.');
        }
    };

    const handleSaveChallenge = async (e) => {
        e.preventDefault();
        if (actionLoading) return;
        setActionLoading(true);
        setError(null);

        try {
            if (currentChallenge.id) {
                // Editar (PUT)
                await challengeService.updateChallenge(currentChallenge.id, currentChallenge);
            } else {
                // Crear (POST)
                await challengeService.createChallenge(currentChallenge);
            }
            // Limpiar y recargar
            setCurrentChallenge(initialChallengeState);
            setIsFormVisible(false);
            await loadChallenges();
        } catch (err) {
            console.error('Error saving challenge:', err);
            setError('Error al guardar el desafío.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteChallenge = async (id) => {
        if (actionLoading) return;
        if (!window.confirm('¿Confirmas la eliminación de este desafío?')) return;
        
        setActionLoading(true);
        setError(null);
        try {
            await challengeService.deleteChallenge(id);
            setChallenges(prev => prev.filter(c => c.id !== id));
        } catch (err) {
            console.error('Error deleting challenge:', err);
            setError('Error al eliminar el desafío.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = (challenge) => {
        setCurrentChallenge(challenge);
        setIsFormVisible(true);
    };

    const handleNew = () => {
        setCurrentChallenge(initialChallengeState);
        setIsFormVisible(true);
    };

    return (
        <div className="mt-4">
            <h4 className="text-lg font-semibold mb-3">Gestión de Desafíos</h4>
            
            <button
                onClick={handleNew}
                className="mb-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50"
                disabled={actionLoading}
            >
                Crear Nuevo Desafío
            </button>

            {/* Formulario de Creación/Edición */}
            {isFormVisible && (
                <form onSubmit={handleSaveChallenge} className="mb-6 p-4 border rounded bg-gray-50 space-y-3">
                    <h5 className="font-bold">{currentChallenge.id ? 'Editar Desafío' : 'Nuevo Desafío'}</h5>
                    
                    <input
                        type="text"
                        placeholder="Título"
                        value={currentChallenge.title}
                        onChange={(e) => setCurrentChallenge({ ...currentChallenge, title: e.target.value })}
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md"
                        disabled={actionLoading}
                    />
                    <textarea
                        placeholder="Descripción"
                        value={currentChallenge.description}
                        onChange={(e) => setCurrentChallenge({ ...currentChallenge, description: e.target.value })}
                        required
                        rows="3"
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md"
                        disabled={actionLoading}
                    />
                    <input
                        type="number"
                        placeholder="Puntos"
                        value={currentChallenge.points}
                        onChange={(e) => setCurrentChallenge({ ...currentChallenge, points: parseInt(e.target.value) })}
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md max-w-[100px]"
                        disabled={actionLoading}
                    />
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            checked={currentChallenge.active}
                            onChange={(e) => setCurrentChallenge({ ...currentChallenge, active: e.target.checked })}
                            id="challenge-active"
                            className="h-4 w-4 text-[color:var(--primary)] border-gray-300 rounded"
                            disabled={actionLoading}
                        />
                        <label htmlFor="challenge-active" className="ml-2 block text-sm text-gray-900">Activo</label>
                    </div>

                    <div className="flex space-x-2">
                        <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[color:var(--primary)] hover:bg-[color:var(--primary-dark)] disabled:opacity-50"
                            disabled={actionLoading}
                        >
                            {actionLoading ? 'Guardando...' : 'Guardar Desafío'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsFormVisible(false)}
                            className="py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            disabled={actionLoading}
                        >
                            Cerrar
                        </button>
                    </div>
                </form>
            )}

            {/* Lista de Desafíos */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puntos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {challenges.map((challenge) => (
                            <tr key={challenge.id}>
                                <td className="px-6 py-4 text-sm text-gray-900">{challenge.title}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{challenge.points}</td>
                                <td className="px-6 py-4 text-sm font-bold text-center">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${challenge.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {challenge.active ? 'Activo' : 'Inactivo'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button
                                        onClick={() => handleEdit(challenge)}
                                        className="text-[color:var(--primary)] hover:text-[color:var(--primary-dark)] disabled:opacity-50"
                                        disabled={actionLoading}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDeleteChallenge(challenge.id)}
                                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                        disabled={actionLoading}
                                    >
                                        Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}